import React, { useMemo } from "react";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch } from "react-instantsearch-dom";
import aa from "search-insights";
import PropTypes from "prop-types";
import axios from "axios";
import qs from "qs";

import UserPresenter from "presenters/UserPresenter";
import { externalRoutes } from "routes";
import { useUsers, useSettings } from "hooks";

const AlgoliaSearchWrapper = ({ proxy, children, context, indexName }) => {
  const { currentUser } = useUsers();
  const {
    settings: { algolia },
  } = useSettings();
  const indexNameWithFallback = indexName || algolia.indexName;
  const searchClient = useMemo(() => {
    const algoliaClient = algoliasearch(algolia.id, algolia.searchKey);
    return {
      ...algoliaClient,
      search(requests) {
        if (requests.every(({ params }) => !params.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              nbPages: 0,
              page: 0,
              processingTimeMS: 0,
            })),
          });
        }

        const processedRequests = requests.map((request) => {
          return {
            ...request,
            indexName: request.indexName?.startsWith("default_") ? indexNameWithFallback : request.indexName,
            params: {
              ...request.params,
              enablePersonalization: true,
              enableReRanking: true,
              ruleContexts: context,
            },
          };
        });

        if (!proxy || UserPresenter.isPaid(currentUser)) return algoliaClient.search(processedRequests);

        return axios
          .get(externalRoutes.algoliaSearchProxy(), {
            params: processedRequests,
            paramsSerializer: (parameters) =>
              qs.stringify(parameters, {
                encode: false,
              }),
          })
          .then((res) => res.data);
      },
    };
  }, [currentUser, algolia, context]);

  aa("init", {
    appId: algolia.id,
    apiKey: algolia.searchKey,
    useCookie: true,
  });

  return (
    <InstantSearch searchClient={searchClient} indexName={indexNameWithFallback}>
      {children}
    </InstantSearch>
  );
};

AlgoliaSearchWrapper.propTypes = {
  proxy: PropTypes.bool,
  children: PropTypes.node.isRequired,
  context: PropTypes.string,
  indexName: PropTypes.string,
};

AlgoliaSearchWrapper.defaultProps = {
  proxy: true,
  context: "",
  indexName: "",
};

export default AlgoliaSearchWrapper;
